import { Component, effect, inject, input, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  BUILDING_PLACEHOLDER_IMAGE,
  INTERACTIVE_MEDIA_CDN_URL,
  SecuredResourceResolverService,
} from '@miq/platform';
import { SecureImage } from '@miq/utils';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, delayWhen, filter, map, retryWhen, scan, switchMap, tap } from 'rxjs/operators';
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { L10nTranslatePipe } from 'angular-l10n';
/*
 * L2-R6: moved from `src/app/shared/components/secured-resource/bg-image/bg-image.component.ts`
 * (7 reports references — the single most-imported reports UI seam) together with
 * SecuredResourceResolverService, which went to @miq/platform because it is a root singleton with
 * two blob caches.
 *
 * The two `@env` reads became tokens (libraries must never import `@env`):
 *   environment.appConstants.BUILDING_PH_IMAGE  -> BUILDING_PLACEHOLDER_IMAGE
 *   environment.interactiveMediaCloudFrontUrl   -> INTERACTIVE_MEDIA_CDN_URL
 * Both are provided by all three shells' app.config.ts.
 *
 * NOTE: @miq/utils ALSO exports a `BgImageComponent` with the SAME `[miq-bg-image]` selector
 * (lib/ng-images/bg-image.component.ts). It is a drifted, simplified variant built on
 * MiqSecuredResourcesService, it is not a drop-in replacement for this one, and nothing in the host
 * or either remote imports it (it is only reachable through the unused MiqImagesModule). The two
 * were NOT merged here — see the L2-R6 report.
 *
 * should pass the image url of the s3 bucket and the component returns the blob url
 * Can also over ride styles in your component
 * @example
 * ```
 * .selected-image-border {
  height: 100px;
  ::ng-deep .image-container {
    width: 100%;
    height: 100%;
  }
}
```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line
  selector: '[miq-bg-image]',
  template: `
    <div class="image-icons">
      @if (showImage() && !publishable && !isPDF) {
        <mat-icon
          class="no-publish-icon"
          [matTooltip]="'recordsSearch.notForPublish' | translate"
          [ngClass]="bgClass()"
          svgIcon="icon-no-publish"
        ></mat-icon>
      }
      <ng-content></ng-content>
    </div>
    @if (showImage()) {
      @if (backgroundImage$ | async; as backgroundImage) {
        @if (!disableBGCoverImage()) {
          <div
            [ngClass]="color"
            class="image-container"
            [ngStyle]="backgroundImage"
          ></div>
        } @else {
          <img
            [alt]="'altRecordImg' | translate"
            class="image"
            [ngClass]="[color]"
            [class.cover-image]="!fullImage()"
            [src]="sanitizedImgSrc$ | async"
            />
          }
        }
      }
    `,
  styleUrl: './bg-image.component.scss',
  imports: [MatIcon, MatTooltip, NgClass, NgStyle, AsyncPipe, L10nTranslatePipe],
})
export class BgImageComponent implements OnInit, OnDestroy {
  private readonly imageRetryCount = 3;
  private readonly initialRetryDelayMs = 300;
  readonly imageSrcInput = input<string>(undefined, { alias: 'imageSrc' });
  imageSrc: string;
  readonly publishableInput = input<boolean>(undefined, { alias: 'publishable' });
  publishable: boolean;
  readonly colorInput = input<string>('default', { alias: 'color' });
  color = 'default';
  private readonly inputSyncEffect = effect(() => {
    const src = this.imageSrcInput();
    if (src !== undefined) this.imageSrc = src;
    const pub = this.publishableInput();
    if (pub != null) this.publishable = pub;
    const col = this.colorInput();
    if (col !== undefined) this.color = col;
    this.imageSrc$.set(this.imageSrc);
  });
  showImage = input(true);
  bgClass = input(''); // TODO: rename to something more meaningful
  /**
   * Disable the div strategy to use an image to keep the real size
   */
  disableBGCoverImage = input(false);
  /**
   * When disableBGCoverImage = true this flag works to define if we resize or not the image
   */
  fullImage = input(true);
  readonly secureImage = input<SecureImage>();
  private readonly secureImageEffect = effect(() => {
    const imageObj = this.secureImage();
    if (imageObj != null) {
      this.imageSrc = typeof imageObj.imageSrc === 'string' ? (imageObj.imageSrc as string) : null;
      this.publishable = imageObj.publishable;
      this.isPDF = imageObj.isPDF;
      this.imageSrc$.set(this.imageSrc);
    }
  });
  public isPDF: boolean;
  private readonly securedResourceResolver = inject(SecuredResourceResolverService);
  private readonly placeholderImage = inject(BUILDING_PLACEHOLDER_IMAGE);
  private readonly interactiveMediaCdnUrl = inject(INTERACTIVE_MEDIA_CDN_URL);
  public imageSrc$ = signal<string>(null);
  // Initialize with building placeholder so alt text never shows
  public sanitizedImgSrc$: Observable<Object> = of(this.placeholderImage);
  public backgroundImage$ = toObservable(this.imageSrc$).pipe(
    filter(url => !!url),
    switchMap(url => {
      if (url.includes(this.interactiveMediaCdnUrl)) {
        this.sanitizedImgSrc$ = of(url);
        return of(this.getBackgroundImageStyle(url));
      }
      return this.loadImage(url).pipe(
        tap(loadedUrl => (this.sanitizedImgSrc$ = of(loadedUrl))),
        catchError(() => {
          // Always fallback to building placeholder
          this.sanitizedImgSrc$ = of(this.placeholderImage);
          return of(this.getBackgroundImageStyle(this.placeholderImage));
        }),
        map(loadedUrl => this.getBackgroundImageStyle(loadedUrl))
      );
    })
  );
  ngOnInit() {
    /* required by Angular-l10n */
  }
  ngOnDestroy() {
    /* required by Angular-l10n */
  }
  loadImage(imageSrc) {
    if (imageSrc === this.placeholderImage) {
      return of(imageSrc);
    }
    return this.securedResourceResolver.loadImage(imageSrc).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= this.imageRetryCount) {
              throw error;
            }
            return retryCount + 1;
          }, 0),
          // Exponential backoff: 300ms, 600ms, 1200ms.
          delayWhen(retryCount => {
            const exponentialFactor = 1 << retryCount;
            return timer(this.initialRetryDelayMs * exponentialFactor);
          })
        )
      ),
      catchError(error => throwError(() => error))
    );
  }
  public getBackgroundImageStyle(imageSrc) {
    if (imageSrc === this.placeholderImage) {
      // if load image catches an error, then image path will be default image and
      //  it has to contain color otherwise the default image will not be seen.
      this.color = this.color === 'image-container' ? 'image-container default' : this.color;
      // for svg coloringq
      return {
        mask: `url(${imageSrc})`,
        '-webkit-mask': `url(${imageSrc})`,
        '-webkit-mask-size': 'cover',
      };
    }
    return {
      'background-image': `url(${imageSrc})`,
      'background-position': 'center center',
      'background-size': 'cover',
      'background-repeat': 'no-repeat',
    };
  }
}
